import {
  createContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  FC,
} from "react";
import axios from "axios";
import liff from "@line/liff";

type LineProfile = Awaited<ReturnType<typeof liff.getProfile>>;

type UserRegistrationContextType = {
  open: boolean;
  lineProfile: LineProfile | null;
  register: () => Promise<void>;
  dismiss: () => void;
};

export const UserRegistrationContext =
  createContext<UserRegistrationContextType>({
    open: false,
    lineProfile: null,
    register: async () => {},
    dismiss: () => {},
  });

type Props = {
  children: ReactNode;
};

export const UserRegistrationProvider: FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [lineProfile, setLineProfile] = useState<LineProfile | null>(null);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const profile = await liff.getProfile();
        setLineProfile(profile);

        const response = await axios.post("/v1/api/mypage/fetch", {
          userId: profile.userId,
        });

        // ユーザーが存在しない場合は自動で新規作成
        if (response.data.Items.length === 0) {
          await axios.post("/v1/api/user/init", {
            userId: profile.userId,
            displayName: profile.displayName,
            pictureUrl: profile.pictureUrl,
          });
        }
      } catch (error) {
        console.error("Error checking user:", error);
      }
    };

    checkUser();
  }, []);

  const register = useCallback(async () => {
    if (!lineProfile) return;
    await axios.post("/v1/api/user/init", {
      userId: lineProfile.userId,
      displayName: lineProfile.displayName,
      pictureUrl: lineProfile.pictureUrl,
    });
    setOpen(false);
  }, [lineProfile]);

  const dismiss = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <UserRegistrationContext.Provider
      value={{ open, lineProfile, register, dismiss }}
    >
      {children}
    </UserRegistrationContext.Provider>
  );
};
